import { useEffect, useRef, useState } from 'react';

import { linkDeWhatsApp } from '@/content/contato';
import { useContato } from '@/hooks/use-conteudo';
import { prefereMenosMovimento } from '@/hooks/use-revelar-ao-rolar';
import { AMBIENTES_DO_FORMULARIO, PASSOS, PRAZOS } from '@/modules/home/orcamento-content';
import {
  formatarTelefone,
  montarMensagem,
  nomeValido,
  telefoneValido,
  type DadosDoOrcamento,
} from '@/modules/home/orcamento-mensagem';

const VAZIO: DadosDoOrcamento = {
  largura: '',
  altura: '',
  prazo: PRAZOS[0],
  observacao: '',
  nome: '',
  telefone: '',
  cidade: '',
  email: '',
};

const CAMPO =
  'border-0 border-b border-bone/25 bg-transparent py-[0.65em] text-[0.95rem] text-bone transition-colors duration-[350ms] ease-plane placeholder:text-bone/30 focus:border-brass focus:outline-none';

const ROTULO = 'text-[0.7rem] tracking-[0.16em] text-bone/55 uppercase';

function Campo({
  id,
  rotulo,
  opcional,
  metade,
  children,
}: {
  id: string;
  rotulo: string;
  opcional?: boolean;
  metade?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`flex flex-col gap-2 ${metade ? 'col-span-full min-[640px]:col-span-1' : 'col-span-full'}`}
    >
      <label htmlFor={id} className={ROTULO}>
        {rotulo}
        {opcional && (
          <em className="ml-1 text-[0.8em] normal-case opacity-70 not-italic">(opcional)</em>
        )}
      </label>
      {children}
    </div>
  );
}

/**
 * Formulário de orçamento em três passos.
 *
 * Vai para o WhatsApp, como no protótipo: não existe módulo de leads na API, e
 * inventar um endpoint aqui criaria uma caixa de entrada que ninguém combinou
 * de olhar. A marcenaria já atende por WhatsApp.
 *
 * Sem `react-hook-form`: são nove campos e duas regras de validação, e o pacote
 * pesaria no bundle de quem só quer ver o site. No painel ele se paga; aqui não.
 *
 * O que foi além do protótipo é o foco: lá a troca de passo só rolava a página,
 * então quem navega por teclado ou leitor de tela ficava com o foco no botão e
 * sem saber que o conteúdo mudou. Aqui o foco vai para o passo novo, que é
 * anunciado junto com a legenda.
 */
export function OrcamentoForm() {
  const contato = useContato();
  const [passo, setPasso] = useState(1);
  const [ambientes, setAmbientes] = useState<readonly string[]>([]);
  const [dados, setDados] = useState<DadosDoOrcamento>(VAZIO);
  const [erro, setErro] = useState<number | null>(null);
  const [enviado, setEnviado] = useState(false);

  const grupoRef = useRef<HTMLFieldSetElement>(null);

  /**
   * Só quando a pessoa pediu para mudar de passo.
   *
   * A versão anterior era uma bandeira de "já montei uma vez", e ela não
   * sobrevive ao StrictMode: em desenvolvimento o React monta, desmonta e monta
   * de novo, então na segunda passada a bandeira já estava levantada e o foco ia
   * para o formulário assim que a página abria — a home carregava direto no
   * orçamento, no fim do site.
   *
   * Amarrar à intenção corrige isso e é mais honesto: o foco existe para avisar
   * quem navega por teclado que o conteúdo mudou, e conteúdo só muda por clique.
   */
  const focarAoTrocar = useRef(false);

  useEffect(() => {
    if (!focarAoTrocar.current) return;

    focarAoTrocar.current = false;
    // `preventScroll` porque `irPara` já decide se e quanto rolar; sem isto o
    // navegador rola por conta própria e as duas rolagens brigam.
    grupoRef.current?.focus({ preventScroll: true });
  }, [passo]);

  function alternarAmbiente(nome: string) {
    setAmbientes((atuais) =>
      atuais.includes(nome) ? atuais.filter((item) => item !== nome) : [...atuais, nome],
    );
  }

  function valido(numero: number): boolean {
    if (numero === 1) return ambientes.length > 0;
    if (numero === 3) return nomeValido(dados.nome) && telefoneValido(dados.telefone);

    return true;
  }

  function irPara(numero: number) {
    setErro(null);
    focarAoTrocar.current = true;
    setPasso(numero);

    const topo =
      (grupoRef.current?.closest('form')?.getBoundingClientRect().top ?? 0) + window.scrollY - 110;

    if (window.scrollY > topo) {
      window.scrollTo({ top: topo, behavior: prefereMenosMovimento() ? 'auto' : 'smooth' });
    }
  }

  function avancar() {
    if (valido(passo)) irPara(passo + 1);
    else setErro(passo);
  }

  function enviar(evento: React.FormEvent) {
    evento.preventDefault();

    if (!valido(3)) {
      setErro(3);
      return;
    }

    const url = linkDeWhatsApp(contato, montarMensagem(dados, ambientes, contato.empresa));

    // Se o navegador bloquear a aba nova, navega na mesma janela.
    if (!window.open(url, '_blank', 'noopener')) window.location.href = url;

    setEnviado(true);
  }

  // O `??` é para o compilador: `passo` só muda por `avancar` e `irPara`, que
  // nunca saem do intervalo, mas indexar por variável devolve `| undefined`.
  const definicao = PASSOS[passo - 1] ?? PASSOS[0];
  const idErro = `orcamento-erro-${passo}`;
  const mostrandoErro = erro === passo;
  const nomeRuim = mostrandoErro && !nomeValido(dados.nome);
  const telefoneRuim = mostrandoErro && !telefoneValido(dados.telefone);

  function aoDigitar(campo: keyof DadosDoOrcamento) {
    return (
      evento: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    ) => setDados((atuais) => ({ ...atuais, [campo]: evento.target.value }));
  }

  return (
    <form
      noValidate
      onSubmit={enviar}
      className="border border-bone/15 bg-bone/5 p-[clamp(24px,3.4vw,44px)]"
    >
      <ol
        aria-label="Progresso do orçamento"
        className="m-0 mb-[clamp(26px,3vw,38px)] flex list-none flex-wrap gap-1.5 p-0"
      >
        {PASSOS.map((item, indice) => {
          const numero = indice + 1;
          const atual = numero === passo;

          return (
            <li
              key={item.numero}
              aria-current={atual ? 'step' : undefined}
              className={`flex items-center gap-2 border py-[0.5em] pr-[0.9em] pl-[0.5em] text-[0.68rem] tracking-[0.14em] uppercase transition-colors duration-[350ms] ${
                atual
                  ? 'border-brass-light/30 bg-brass-light/[0.07] text-brass-light'
                  : `border-transparent ${numero < passo ? 'text-bone/70' : 'text-bone/40'}`
              }`}
            >
              <span className="grid h-6 w-6 place-items-center rounded-full border border-current text-[0.58rem]">
                {item.numero}
              </span>
              {item.rotulo}
            </li>
          );
        })}
      </ol>

      {/* `key` força a remontagem a cada passo, e é o que faz a animação repetir. */}
      <fieldset
        key={passo}
        ref={grupoRef}
        tabIndex={-1}
        className="passo-entra m-0 border-0 p-0 focus:outline-none"
      >
        <legend className="mb-[1.4rem] p-0 font-display text-[clamp(1.2rem,2vw,1.6rem)] tracking-[-0.015em] text-bone">
          {definicao.legenda}
        </legend>

        {passo === 1 && (
          <div className="grid grid-cols-2 gap-2.5 min-[640px]:grid-cols-[repeat(auto-fill,minmax(140px,1fr))]">
            {AMBIENTES_DO_FORMULARIO.map((nome) => {
              const marcado = ambientes.includes(nome);

              return (
                <label key={nome} className="relative cursor-pointer">
                  <input
                    type="checkbox"
                    name="ambiente"
                    value={nome}
                    checked={marcado}
                    onChange={() => alternarAmbiente(nome)}
                    aria-describedby={mostrandoErro ? idErro : undefined}
                    className="peer absolute h-0 w-0 opacity-0"
                  />
                  <span
                    className={`block border px-[1.1em] py-[1em] text-[0.86rem] transition-all duration-300 ease-plane peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brass ${
                      marcado
                        ? 'border-brand-500 bg-brand-600 text-white'
                        : 'border-bone/20 text-bone/80 hover:border-bone/40'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`mr-2.5 inline-block h-2 w-2 border align-[1px] transition-all duration-300 ${
                        marcado ? 'border-brass-light bg-brass-light' : 'border-bone/40'
                      }`}
                    />
                    {nome}
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {passo === 2 && (
          <div className="grid grid-cols-1 gap-4 min-[640px]:grid-cols-2">
            <Campo id="orc-largura" rotulo="Largura aproximada (m)" metade>
              <input
                id="orc-largura"
                inputMode="decimal"
                placeholder="ex.: 3,40"
                value={dados.largura}
                onChange={aoDigitar('largura')}
                className={CAMPO}
              />
            </Campo>

            <Campo id="orc-altura" rotulo="Pé-direito (m)" metade>
              <input
                id="orc-altura"
                inputMode="decimal"
                placeholder="ex.: 2,70"
                value={dados.altura}
                onChange={aoDigitar('altura')}
                className={CAMPO}
              />
            </Campo>

            <Campo id="orc-prazo" rotulo="Quando pretende instalar?">
              <select
                id="orc-prazo"
                value={dados.prazo}
                onChange={aoDigitar('prazo')}
                className={`${CAMPO} cursor-pointer`}
              >
                {PRAZOS.map((opcao) => (
                  <option key={opcao} value={opcao} className="bg-brand-900 text-bone">
                    {opcao}
                  </option>
                ))}
              </select>
            </Campo>

            <Campo id="orc-obs" rotulo="Algum detalhe importante?" opcional>
              <textarea
                id="orc-obs"
                rows={3}
                placeholder="Apartamento na planta, parede fora de esquadro, quero laca fosca verde..."
                value={dados.observacao}
                onChange={aoDigitar('observacao')}
                className={`${CAMPO} resize-y leading-[1.55]`}
              />
            </Campo>
          </div>
        )}

        {passo === 3 && (
          <div className="grid grid-cols-1 gap-4 min-[640px]:grid-cols-2">
            <Campo id="orc-nome" rotulo="Nome">
              <input
                id="orc-nome"
                autoComplete="name"
                required
                value={dados.nome}
                onChange={aoDigitar('nome')}
                aria-invalid={nomeRuim || undefined}
                aria-describedby={mostrandoErro ? idErro : undefined}
                className={`${CAMPO} ${nomeRuim ? 'border-b-[#e2725b]' : ''}`}
              />
            </Campo>

            <Campo id="orc-fone" rotulo="WhatsApp" metade>
              <input
                id="orc-fone"
                type="tel"
                autoComplete="tel"
                required
                placeholder="(45) 90000-0000"
                value={dados.telefone}
                onChange={(evento) =>
                  setDados((atuais) => ({
                    ...atuais,
                    telefone: formatarTelefone(evento.target.value),
                  }))
                }
                aria-invalid={telefoneRuim || undefined}
                aria-describedby={mostrandoErro ? idErro : undefined}
                className={`${CAMPO} ${telefoneRuim ? 'border-b-[#e2725b]' : ''}`}
              />
            </Campo>

            <Campo id="orc-cidade" rotulo="Cidade" metade>
              <input
                id="orc-cidade"
                placeholder="Cascavel / PR"
                value={dados.cidade}
                onChange={aoDigitar('cidade')}
                className={CAMPO}
              />
            </Campo>

            <Campo id="orc-email" rotulo="E-mail" opcional>
              <input
                id="orc-email"
                type="email"
                autoComplete="email"
                value={dados.email}
                onChange={aoDigitar('email')}
                className={CAMPO}
              />
            </Campo>
          </div>
        )}

        {mostrandoErro && (
          <p id={idErro} role="alert" className="mt-4 text-[0.8rem] text-[#e2725b]">
            {passo === 1
              ? 'Escolha pelo menos um ambiente para seguir.'
              : 'Preencha o nome e o WhatsApp para enviar.'}
          </p>
        )}

        {passo === 3 && (
          <p className="mt-5 text-[0.74rem] leading-[1.5] text-bone/40">
            Seus dados servem só para responder este orçamento. Nada de lista de e-mail.
          </p>
        )}
      </fieldset>

      <div className="mt-[clamp(26px,3vw,38px)] flex flex-col-reverse items-stretch gap-4 border-t border-bone/15 pt-[clamp(20px,2.4vw,28px)] min-[640px]:flex-row min-[640px]:flex-wrap min-[640px]:items-center min-[640px]:gap-5">
        {passo > 1 && (
          <button
            type="button"
            onClick={() => irPara(passo - 1)}
            className="self-center pb-1 text-[0.82rem] tracking-[0.06em] text-bone uppercase opacity-70 [background:linear-gradient(currentColor,currentColor)_0_100%/100%_1px_no-repeat] transition-[background-size] duration-[400ms] ease-plane-slow hover:[background-size:0%_1px] min-[640px]:self-auto"
          >
            Voltar
          </button>
        )}

        <button
          type={passo === PASSOS.length ? 'submit' : 'button'}
          onClick={passo === PASSOS.length ? undefined : avancar}
          className="group relative isolate inline-flex items-center justify-center gap-[0.7em] overflow-hidden bg-brass px-[1.6em] py-[1.05em] text-[0.82rem] font-medium tracking-[0.06em] text-brand-950 uppercase transition-transform duration-[350ms] ease-plane active:translate-y-px min-[640px]:ml-auto min-[640px]:w-auto"
        >
          <span
            aria-hidden="true"
            className="absolute inset-0 -z-10 translate-y-full bg-bone transition-transform duration-[450ms] ease-plane-slow group-hover:translate-y-0 motion-reduce:transition-none"
          />
          {passo === PASSOS.length ? 'Enviar pelo WhatsApp' : 'Continuar'}
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="h-[1.15em] w-[1.15em] transition-transform duration-[350ms] ease-plane group-hover:translate-x-1 motion-reduce:transition-none"
          >
            <path
              d="M4 12h15m-6-6 6 6-6 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {enviado && (
        <p
          role="status"
          className="mt-[18px] border-l-2 border-brass pl-3.5 text-[0.88rem] text-brass-light"
        >
          Pedido montado. Abrimos o WhatsApp com o resumo — é só apertar enviar.
        </p>
      )}
    </form>
  );
}
